import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { switchBff, getCurrentBffType } from './config/api';
import Login from './components/Login';
import AdminDashboard from './components/AdminDashboard';
import UserDashboard from './components/UserDashboard';
import './App.css';

// BFF Initialization Component
const BffInitializer = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Get BFF parameter from URL
    const urlParams = new URLSearchParams(location.search);
    const bffParam = urlParams.get('bff');
    
    // Determine which BFF to use
    let bffType = 'prod'; // Default to prod for production
    
    if (bffParam === 'prod') {
      bffType = 'prod';
    } else if (bffParam === 'mock') {
      bffType = 'mock';
    }
    // If no parameter or invalid parameter, default to prod
    
    // Switch to the determined BFF
    const currentBff = getCurrentBffType();
    if (currentBff !== bffType) {
      console.log(`🔄 Switching BFF from ${currentBff} to ${bffType} based on URL parameter`);
      switchBff(bffType);
    }
  }, [location.search]);

  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="app">
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Login Route Component
const LoginRoute = () => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="app">
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated()) {
    if (isAdmin()) {
      return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <Login onLoginSuccess={() => {
    // The redirect will happen automatically due to the authentication state change
  }} />;
};

// Main App Component
const App = () => {
  // Log build timestamp for debugging
  const buildTime = process.env.REACT_APP_BUILD_TIME || 'unknown';
  console.log('🚀 Aerotubos2025 App loaded - Build:', buildTime);
  
  return (
    <AuthProvider>
      <Router>
        <BffInitializer>
          <div className="app">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginRoute />} />
              
              {/* Protected Routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <UserDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Default Route */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </div>
        </BffInitializer>
      </Router>
    </AuthProvider>
  );
};

export default App;